/* Catalog artifact card — the "Spec sheet" (Direction B) from the Chat Artifact
   Cards design, ported to canonical --rv-* tokens so it reads the same in light
   and dark. Each entity (bundle / module / component) gets a distinct accent
   sourced from the primitive color ramps (mode-stable) + their alpha variants. */

.Card {
  overflow: hidden;
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-xl);
  background: var(--rv-surface);
  box-shadow: var(--rv-shadow-sm);
}

/* entity accent tokens */
.Bundle {
  --ent: var(--color-orange-500);
  --ent-ink: var(--color-orange-700);
  --ent-soft: var(--color-orange-500-alpha-10);
  --ent-line: var(--color-orange-500-alpha-25);
}

.Module {
  --ent: var(--color-blue-500);
  --ent-ink: var(--color-blue-700);
  --ent-soft: var(--color-blue-500-alpha-10);
  --ent-line: var(--color-blue-500-alpha-25);
}

.Component {
  --ent: var(--color-cyan-500);
  --ent-ink: var(--color-cyan-700);
  --ent-soft: var(--color-cyan-500-alpha-10);
  --ent-line: var(--color-cyan-500-alpha-25);
}

/* dark mode: lift the ink so labels stay legible on dark surfaces */
:global(html[data-color-mode='dark']) .Bundle {
  --ent-ink: var(--color-orange-300);
}

:global(html[data-color-mode='dark']) .Module {
  --ent-ink: var(--color-blue-300);
}

:global(html[data-color-mode='dark']) .Component {
  --ent-ink: var(--color-cyan-300);
}

.Sdraft {
  border-color: var(--ent-line);
}

.Sattention {
  border-color: var(--rv-warning-ink);
}

.Sconfirmed {
  opacity: 96%;
}

/* publish proposal: the entity goes live — a positive, success-toned accent */
.Spublished {
  border-color: var(--rv-success);
}

/* ---- banded header ---- */
.Band {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--rv-space-3);
  padding: var(--rv-space-3) var(--rv-space-4);
  background: var(--ent-soft);
  border-bottom: 1px solid var(--ent-line);
}

.Band::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: var(--rv-space-1);
  background: var(--ent);
}

.Sconfirmed .Band::before {
  background: var(--rv-success);
}

.Sattention .Band::before {
  background: var(--rv-warning);
}

.Spublished .Band::before {
  background: var(--rv-success);
}

.BandIcon {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: var(--rv-space-8);
  height: var(--rv-space-8);
  border-radius: var(--rv-radius-md);
  background: var(--rv-surface);
  color: var(--ent-ink);
  box-shadow: inset 0 0 0 1px var(--ent-line);
}

.BandIcon svg {
  width: var(--rv-icon-sm);
  height: var(--rv-icon-sm);
}

.BandTitles {
  flex: 1;
  min-width: 0;
}

.Kicker {
  font-family: var(--rv-font-sans);
  font-size: var(--rv-text-xxs);
  font-weight: var(--rv-w-bold);
  letter-spacing: var(--rv-track-widest);
  text-transform: uppercase;
  color: var(--ent-ink);
}

.Name {
  display: flex;
  align-items: center;
  gap: var(--rv-space-2);
  margin-top: var(--space-3xs);
  font-family: var(--rv-font-display);
  font-weight: var(--rv-w-medium);
  font-size: var(--rv-text-md);
  letter-spacing: var(--rv-track-tight);
  color: var(--rv-fg-1);
}

.Ver {
  font-family: var(--rv-font-mono);
  font-size: var(--rv-text-xxs);
  font-weight: var(--rv-w-medium);
  color: var(--ent-ink);
  background: var(--rv-surface);
  border-radius: var(--rv-radius-sm);
  padding: 0 var(--space-xxs);
  box-shadow: inset 0 0 0 1px var(--ent-line);
}

/* ---- state chip ---- */
.StateChip {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--space-xxs);
  padding: var(--space-xxs) var(--rv-space-2);
  border-radius: var(--rv-radius-full);
  font-size: var(--rv-text-xxs);
  font-weight: var(--rv-w-semibold);
}

.StateChip svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

.StateDraft {
  background: var(--rv-surface);
  color: var(--ent-ink);
  box-shadow: inset 0 0 0 1px var(--ent-line);
}

.StateConfirmed {
  background: var(--rv-success-bg);
  color: var(--rv-success-ink);
}

.StateAttention {
  background: var(--rv-warning-bg);
  color: var(--rv-warning-ink);
}

.StatePublished {
  background: var(--rv-success-bg);
  color: var(--rv-success-ink);
}

/* ---- body ---- */
.Body {
  display: grid;
  gap: var(--rv-space-3);
  padding: var(--rv-space-4);
}

.Desc {
  margin: 0;
  font-size: var(--rv-text-sm);
  line-height: var(--line-height-lg);
  color: var(--rv-fg-2);
}

/* auto-fit: a lone fact (e.g. Billing periods) spans the full width; two or more
   pack into columns. */
.Facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: 0 var(--rv-space-5);
}

.Fact {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--rv-space-3);
  padding: var(--rv-space-2) 0;
  border-bottom: 1px solid var(--rv-border-subtle);
  font-size: var(--rv-text-sm);
}

.FactKey {
  color: var(--rv-fg-3);
  white-space: nowrap;
}

.FactValue {
  color: var(--rv-fg-1);
  font-weight: var(--rv-w-medium);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.Mono {
  font-family: var(--rv-font-mono);
  font-size: var(--rv-text-xs);
}

/* ---- sections (refs, components, matrix) ---- */
.Section {
  display: grid;
  gap: var(--rv-space-2);
}

.SectionLabel {
  font-family: var(--rv-font-sans);
  font-size: var(--rv-text-xxs);
  font-weight: var(--rv-w-bold);
  letter-spacing: var(--rv-track-wide);
  text-transform: uppercase;
  color: var(--rv-fg-4);
}

.Refs {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rv-space-2);
}

.Ref {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xxs);
  padding: var(--space-xxs) var(--rv-space-2);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  background: var(--rv-bg-app-subtle);
  color: var(--rv-fg-1);
  font-size: var(--rv-text-xs);
  font-weight: var(--rv-w-medium);
}

.Ref svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
  color: var(--ent-ink);
}

.RefVer {
  font-family: var(--rv-font-mono);
  font-size: var(--rv-text-xxs);
  color: var(--rv-fg-4);
}

/* attached components */
.Comps {
  display: grid;
  gap: var(--rv-space-2);
}

.Comp {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: var(--rv-space-3);
  padding: var(--rv-space-2) var(--rv-space-3);
  border: 1px solid var(--rv-border);
  border-radius: var(--rv-radius-md);
  background: var(--rv-bg-app-subtle);
  font-size: var(--rv-text-xs);
}

.CompName {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xxs);
  font-weight: var(--rv-w-semibold);
  color: var(--rv-fg-1);
}

.CompName svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
  color: var(--color-cyan-500);
}

.CompIncl {
  color: var(--rv-fg-3);
  white-space: nowrap;
}

.CompPrice {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xxs);
  color: var(--rv-fg-1);
  font-weight: var(--rv-w-semibold);
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}

.CompPrice svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

.CompMissing {
  border-color: var(--rv-warning-ink);
  background: var(--rv-warning-bg);
}

.CompMissing .CompPrice {
  color: var(--rv-warning-ink);
}

/* ---- price matrix: period × market ---- */
.Matrix {
  display: grid;
  grid-template-columns: minmax(0, auto) repeat(var(--rv-matrix-columns), minmax(0, 1fr));
  gap: var(--space-3xs);
  background: var(--rv-border-subtle);
  border: 1px solid var(--rv-border-subtle);
  border-radius: var(--rv-radius-lg);
  overflow: hidden;
}

/* text-align (not flex) drives horizontal alignment so per-type rules aren't
   out-specified by this child-combinator selector — headers and value cells
   then line up in the same column. */
.Matrix > span {
  padding: var(--rv-space-2) var(--rv-space-3);
  background: var(--rv-surface);
  font-size: var(--rv-text-xs);
  font-variant-numeric: tabular-nums;
}

.MatrixCorner {
  background: var(--rv-bg-app-subtle);
}

.MatrixHead {
  text-align: right;
  background: var(--rv-bg-app-subtle);
  font-size: var(--rv-text-xxs);
  font-weight: var(--rv-w-bold);
  letter-spacing: var(--rv-track-wide);
  text-transform: uppercase;
  color: var(--rv-fg-4);
}

.MatrixRowLabel {
  background: var(--rv-bg-app-subtle);
  color: var(--rv-fg-3);
  font-weight: var(--rv-w-medium);
}

.MatrixCell {
  text-align: right;
}

.MatrixCell b {
  display: block;
  font-weight: var(--rv-w-semibold);
  color: var(--rv-fg-1);
}

.MatrixCell em {
  display: block;
  font-style: normal;
  font-size: var(--rv-text-xxs);
  color: var(--rv-fg-3);
}

.MatrixCellMissing b {
  color: var(--rv-warning-ink);
  font-weight: var(--rv-w-medium);
}

/* ---- headline price ---- */
.Price {
  display: flex;
  align-items: baseline;
  gap: var(--rv-space-2);
}

.PriceLabel {
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-3);
  font-weight: var(--rv-w-medium);
}

.PriceAmount {
  margin-left: auto;
  font-family: var(--rv-font-sans);
  font-weight: var(--rv-w-bold);
  font-size: var(--rv-text-lg);
  letter-spacing: var(--rv-track-tight);
  color: var(--rv-fg-1);
  font-variant-numeric: tabular-nums;
}

.PriceAmount small {
  font-size: var(--rv-text-xs);
  font-weight: var(--rv-w-medium);
  color: var(--rv-fg-3);
}

/* ---- gap panel ---- */

/*
 * The itemised review for a composite proposal: one numbered row per write the
 * single Approve will perform. Deliberately plain — it is a checklist to read
 * before an irreversible publish, not a decorative summary.
 */
.Steps {
  display: grid;
  gap: var(--rv-space-1);
  counter-reset: rv-step;
}

.Step {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: baseline;
  gap: var(--rv-space-2);
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-2);
}

.Step::before {
  counter-increment: rv-step;
  content: counter(rv-step);
  min-width: var(--rv-space-5);
  padding: 0 var(--rv-space-1);
  border-radius: var(--rv-radius-sm);
  background: var(--rv-bg-app-subtle);
  border: 1px solid var(--rv-border);
  color: var(--rv-fg-4);
  font-family: var(--rv-font-mono);
  font-size: var(--rv-text-xxs);
  text-align: center;
}

.Gap {
  border: 1px solid var(--rv-warning-ink);
  border-radius: var(--rv-radius-md);
  overflow: hidden;
}

.GapHead {
  display: flex;
  align-items: center;
  gap: var(--rv-space-2);
  padding: var(--rv-space-2) var(--rv-space-3);
  background: var(--rv-warning-bg);
  color: var(--rv-warning-ink);
  font-size: var(--rv-text-xs);
  font-weight: var(--rv-w-semibold);
}

.GapHead svg {
  width: var(--rv-icon-xs);
  height: var(--rv-icon-xs);
}

.GapRow {
  padding: var(--rv-space-2) var(--rv-space-3);
  border-top: 1px solid var(--rv-border-subtle);
  font-size: var(--rv-text-xs);
  color: var(--rv-fg-2);
}

/* ---- action bar ---- */
.Bar {
  display: flex;
  align-items: center;
  gap: var(--rv-space-2);
  padding: var(--rv-space-3) var(--rv-space-4);
  border-top: 1px solid var(--rv-border);
  background: var(--rv-bg-app-subtle);
}
